import { useEffect, useState } from "react";

type Option = { value: string; label: string };
export type CityOption = Option & { lat?: number; lng?: number };

export function useCountries(): Option[] {
	const [options, setOptions] = useState<Option[]>([]);
	useEffect(() => {
		import("country-state-city").then(({ Country }) => {
			setOptions(
				Country.getAllCountries().map((c) => ({
					value: c.isoCode,
					label: c.name,
				})),
			);
		});
	}, []);
	return options;
}

export function useCitiesOfCountry(isoCode: string): CityOption[] {
	const [options, setOptions] = useState<CityOption[]>([]);
	useEffect(() => {
		if (!isoCode) {
			setOptions([]);
			return;
		}
		import("country-state-city").then(({ City }) => {
			const seen = new Set<string>();
			setOptions(
				(City.getCitiesOfCountry(isoCode) ?? [])
					.filter((c) => {
						if (seen.has(c.name)) return false;
						seen.add(c.name);
						return true;
					})
					.map((c) => ({
						value: c.name,
						label: c.name,
						lat: c.latitude ? Number(c.latitude) : undefined,
						lng: c.longitude ? Number(c.longitude) : undefined,
					})),
			);
		});
	}, [isoCode]);
	return options;
}

export function useCityCoords(
	country: string | null | undefined,
	city: string | null | undefined,
): { lat: number; lng: number } | null {
	const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
		null,
	);
	useEffect(() => {
		if (!country || !city) {
			setCoords(null);
			return;
		}
		import("country-state-city").then(({ City }) => {
			const match = (City.getCitiesOfCountry(country) ?? []).find(
				(c) => c.name === city,
			);
			if (match?.latitude && match?.longitude) {
				setCoords({
					lat: Number(match.latitude),
					lng: Number(match.longitude),
				});
			} else {
				setCoords(null);
			}
		});
	}, [country, city]);
	return coords;
}
